import { Parser, Grammar } from "nearley";
import { uniq, flatten, pickBy, some, zipObject, map, values } from "../utils/lodash";
import { InterviewState } from "../models/states/InterviewState";
import { LogicModel } from "../models/LogicModel";
import { QuestionModel } from "../models/QuestionModel";
import { BlockModel } from "../models/BlockModel";
import { INVALID, getKeywords } from "../grammar/utils/consts";
import { Piping } from "../piping";
import { RecallType } from "../piping/RecallType";
import { LogicType } from "../models/enums/LogicType";
import { ParserState } from "../grammar/utils/ParserState";
import { EvaluationResult } from "./EvaluationResult";
import { getSettingBoolean, hasSetting } from "../models/utilities/settingUtilities";
// @ts-ignore
import * as grammar from "../grammar/expressions.ne";
import { ActionTypes } from "../redux/actions/types";
import { invalidateClientVariables } from "../redux/actions";

// Compile grammar for basic and advanced expressions.
const advCompiled = Grammar.fromCompiled(grammar);
grammar.ParserStart = "basic_expression";
const basicCompiled = Grammar.fromCompiled(grammar);

// TODO: This cache needs to be stored in redux.
/**
 * Clears the expressions cache.
 */
export const clearCache = () => {
    cache = INITIAL_CACHE_STATE;
};

// Keep a cache of expression evaluations.
const INITIAL_CACHE_STATE = {
    resultById: {},
    resultIdsByVariableName: {},
    resultIdByKey: {},
};
let cache = INITIAL_CACHE_STATE;
let _id = 0;
const newId = () => _id++;
const keyForExpression = (expression: string, simple?: boolean) =>
    simple ? `simple:${expression}` : expression;

/**
 * Invalidates the cache for variables of an expression.
 */
export const invalidateCache = (...variables: string[]) => {
    const a = variables.map((v) => cache.resultIdsByVariableName[v.toUpperCase()] || []);
    const b = flatten(a);
    const ids = uniq(b) as number[];
    if (ids.length === 0) return;
    cache = {
        resultById: pickBy(cache.resultById, (value, key) => ids.indexOf(parseInt(key)) === -1),
        resultIdByKey: pickBy(
            cache.resultIdByKey,
            (value: number, key) => ids.indexOf(value) === -1,
        ),
        resultIdsByVariableName: pickBy(
            cache.resultIdsByVariableName,
            (value, key) => !some(variables, (v) => v.toUpperCase() === key.toUpperCase()),
        ),
    };
};

export function cacheEvaluation(key: string, result: string, variableDependencies: string[]) {
    const resultId = newId();
    cache = {
        resultById: {
            ...cache.resultById,
            [resultId]: result,
        },
        resultIdByKey: {
            ...cache.resultIdByKey,
            [key]: resultId,
        },
        resultIdsByVariableName: {
            ...cache.resultIdsByVariableName,
            ...zipObject(
                variableDependencies,
                map(variableDependencies, (v) => [
                    ...(cache.resultIdsByVariableName[v] || []),
                    resultId,
                ]),
            ),
        },
    };
    return resultId;
}

export function findCachedEvaluation(key: string) {
    return cache.resultById[cache.resultIdByKey[key]];
}

/**
 * Parses an expression, returning a result.
 */
export const parse = (
    expression: string,
    parserState: ParserState,
    type: LogicType = LogicType.Advanced,
) => {
    try {
        const simple = type === LogicType.Simple;
        // Parse piping for simple expressions.
        if (simple)
            expression = Piping.evaluateRecalls(parserState.interview, expression, RecallType.Code);
        // Remove leading and trailing spaces.
        expression = expression.trim();
        // Try to close all brackets.
        const openingBrackets = (expression.match(/\(/g) || []).length;
        const closingBrackets = (expression.match(/\)/g) || []).length;
        if (openingBrackets > closingBrackets)
            expression += ")".repeat(openingBrackets - closingBrackets);
        else if (closingBrackets > openingBrackets) {
            for (let i = closingBrackets; i > openingBrackets; i--) {
                const index = expression.lastIndexOf(")");
                if (index > -1)
                    expression = expression.slice(0, index) + expression.slice(index + 1);
            }
        }
        // Select compiled expression parser.
        const compiled = simple ? basicCompiled : advCompiled;
        const parser = new Parser(compiled);
        // Feed expression.
        parser.feed(expression.toLowerCase());
        // Return results.
        if (parser.results[0] == null) return INVALID;
        if (parser.results.length > 1)
            // eslint-disable-next-line no-console
            console.warn(
                `Evaluator: grammar for ${expression} was ambiguous; found ${parser.results.length} results.`,
            );
        // Evaluate by passing state if we receive a function.
        if (typeof parser.results[0] === "function") return parser.results[0](parserState);
        return parser.results[0];
    } catch (ex) {
        // eslint-disable-next-line no-console
        console.warn(`${expression}: ${ex}`);
        return INVALID;
    }
};

/**
 * Evaluates an expression in an unsafe way, returning a value in string or number.
 * Unsafe means that the evaluation will yield side-effects.
 * Side-effects will be dispatched using the dispatch function passed as a parameter.
 * For example, MVC will set variables.
 * @deprecated Use thunk instead.
 */
export function __evaluateUnsafe__(
    getInterview: () => InterviewState,
    dispatch: (action: ActionTypes) => ActionTypes,
    logic: LogicModel,
): EvaluationResult;
export function __evaluateUnsafe__(
    getInterview: () => InterviewState,
    dispatch: (action: ActionTypes) => ActionTypes,
    logic: string,
    simple?: boolean,
): EvaluationResult;
export function __evaluateUnsafe__(
    getInterview: () => InterviewState,
    dispatch: (action: ActionTypes) => ActionTypes,
    logic: LogicModel | string,
    simple?: boolean,
) {
    simple = simple || false;
    if (typeof logic === "object") {
        simple = logic.type === LogicType.Simple;
        logic = logic.expression;
    }
    // Try to find the result in cache.
    const key = keyForExpression(logic, simple);
    let resultId = cache.resultIdByKey[key];
    if (resultId != null) return new EvaluationResult(cache.resultById[resultId]);
    // Evaluate the expression.
    const variables = [];
    let invalidateCache = false;
    const state: ParserState = {
        interview: getInterview(),
        didParseVariable: (variable: string) => {
            variables.push(variable.toUpperCase());
        },
        invalidateCache: () => {
            invalidateCache = true;
            dispatch(invalidateClientVariables());
        },
        dispatch: (action: ActionTypes) => {
            dispatch(action);
            state.interview = getInterview();
        },
    };
    const result = parse(logic, state, simple ? LogicType.Simple : LogicType.Advanced);
    // The evaluated result is not cacheable.
    if (invalidateCache) return new EvaluationResult(result);
    // Save in cache.
    cacheEvaluation(key, result, variables);
    return new EvaluationResult(result);
}

/**
 * Evaluates logic in a safe way. This eliminates all side-effects from the evaluated logic.
 * For example, MVC will not set variables.
 * Use this in display logic or other logic which should not have any side-effects.
 * @deprecated Use thunk instead.
 */
export function __evaluateSafe__(interview: InterviewState, logic: LogicModel): EvaluationResult;
export function __evaluateSafe__(
    interview: InterviewState,
    logic: string,
    simple?: boolean,
): EvaluationResult;
export function __evaluateSafe__(
    interview: InterviewState,
    logic: LogicModel | string,
    simple?: boolean,
) {
    simple = simple || false;
    if (typeof logic === "object") {
        simple = logic.type === LogicType.Simple;
        logic = logic.expression;
    }
    return __evaluateUnsafe__(
        () => interview,
        (a) => null,
        logic,
        simple,
    );
}

/**
 * Finds all variables in an expression.
 * You can optionally include system variables with inclSystem.
 */
export const findVariables = (expression: string | LogicModel): string[] => {
    if (!expression) return [];
    if (typeof expression !== "string") {
        expression = expression.expression;
    }
    const keywords = values(getKeywords());
    const variables = uniq(
        expression.match(/\b[a-zA-Z]\w*\b/g)?.map((v) => v.toUpperCase()),
    ).filter((v) => !keywords.some((k) => k.toUpperCase() === v));
    return variables;
};

/**
 * @deprecated Use thunk instead.
 */
export const __isQuestionVisible__ = (question: QuestionModel, interview: InterviewState) => {
    if (
        interview.navigation.currentPage &&
        interview.navigation.currentPage.previewQuestion === question.name
    ) {
        return true;
    }

    if (hasSetting(question, "Active") && !getSettingBoolean(question, "Active")) {
        return false;
    }

    if (
        interview.navigation.currentPageOnServer?.questions?.some((q) => q.name === question.name)
    ) {
        return true;
    }

    const displayLogicEvaluation =
        question.displayLogic === undefined ||
        __evaluateSafe__(interview, question.displayLogic).asBoolean();

    const skipLogicEvaluation =
        question.skipLogic === undefined ||
        !__evaluateSafe__(interview, question.skipLogic).asBoolean();

    return displayLogicEvaluation && skipLogicEvaluation;
};

/**
 * @deprecated Use thunk instead.
 */
export const __isBlockVisible__ = (block: BlockModel, interview: InterviewState) =>
    block.questions.length > 0 &&
    (block.displayLogic === undefined ||
        __evaluateSafe__(interview, block.displayLogic).asBoolean()) &&
    (block.skipLogic === undefined || !__evaluateSafe__(interview, block.skipLogic).asBoolean());
